import { ReactElement, useRef, memo, useCallback, Fragment, useMemo } from 'react'
import { reduce } from 'lodash-es'
import { Group } from 'three'
import shallow from 'zustand/shallow'
import { useTheme } from '@mui/material/styles'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { Html } from '@react-three/drei'
import { ThreeEvent } from '@react-three/fiber'
import { useTriggerContextMenu } from '@ui/hooks'
import { useAppStore } from '@stores'
import { useModelStore } from 'src/components/pages/Editor/stores/modelStore'
import { useSceneStore } from 'src/components/pages/Editor/stores/sceneStore'
import ArrowOrientationSlab from '../ArrowOrientationSlab'
import DomainMesh from '../DomainMesh'
import LiveLoadIndicator from '../LiveLoadIndicator'
import ManualLoadIndicator from '../ManualLoadIndicator'
import MemberMeshElement from '../MemberMeshElement'
import ShapeMesh from '../ShapeMesh'
import SnowLoadIndicator from '../SnowLoadIndicator'
import { modelName } from '../constants'
import { getPolygonNormal } from '../utils'
import { beamThickness, typeToPolygonOffsetFactor } from './constants'

// Element types whose bodies are rendered by BatchedShapesMesh (ModelMeshV2)
// when batched model rendering is active. Their overlays still render here.
const BATCHED_SHAPE_TYPES = new Set<ElementTypes>([
  'outer_walls',
  'inner_walls',
  'slabs',
  'roof_slabs',
  'vertical_slabs',
  'vertical_roof_slabs',
])

interface ModelMeshProps {
  model: PlanarModel
  translucent?: boolean
  invisible?: boolean
  applyThickness?: boolean
  interactableByType: VisibilityByType
  blockingElements: BlockingByType
  onClick: (event: ThreeEvent<MouseEvent>) => void
  onPointerOver: (event: ThreeEvent<MouseEvent>) => void
  onPointerLeave: (event: ThreeEvent<MouseEvent>) => void
  hiddenElements?: Set<string>
  selectedElementIds: Set<string>
  highlightedIds: Set<string>
  highlightedSecondaryIds?: Set<string>
  noPointerInteractions?: boolean
  guidToCrossSection: Record<string, ElementCSAssignment>
  /**
   * Skip rendering the batched shape bodies here (walls, slabs, roof slabs,
   * vertical slabs, vertical roof slabs). Used when the experimental batched
   * model rendering (`BatchedShapesMesh` in `ModelMeshV2`) is active, so those
   * bodies are not drawn twice. Their overlays (load arrows, indicators) still
   * render here.
   */
  skipBatchedShapes?: boolean
}

const ModelMesh = ({
  model,
  onClick,
  onPointerOver,
  onPointerLeave,
  interactableByType,
  blockingElements,
  translucent,
  invisible,
  hiddenElements = new Set(),
  selectedElementIds,
  highlightedIds,
  highlightedSecondaryIds = new Set(),
  applyThickness,
  noPointerInteractions = false,
  guidToCrossSection,
  skipBatchedShapes = false,
}: ModelMeshProps): ReactElement | null => {
  const { scenePalette, sceneOffsets } = useTheme()
  const groupRef = useRef<Group>(null)

  const { openContextMenuViaEvent } = useTriggerContextMenu()

  // Hooks for transparency
  const { visibilityByType: transparencyVisibilityByType, guidToTransparent } = useModelStore(
    state => state.transparencyVisibilityModel,
    shallow,
  )
  const transparentOpacity = useAppStore(state => state.transparentOpacityV2 ?? 0.1)
  const getTransparencyOpacity = useCallback(
    (guid: string, elementType?: ElementTypes) => {
      if (!translucent) return 1

      // Priority: guidToTransparent has higher priority than visibilityByType
      const isTransparentForGuid = guidToTransparent[guid]
      if (isTransparentForGuid !== undefined) {
        // Use fixed opacity values: transparentOpacity for transparent, 1 for notTransparent
        return isTransparentForGuid ? transparentOpacity : 1
      }

      // Fallback to type-based transparency
      if (!elementType) return 1
      const isTransparentForType = transparencyVisibilityByType[elementType]
      return isTransparentForType ? transparentOpacity : 1
    },
    [translucent, transparencyVisibilityByType, guidToTransparent, transparentOpacity],
  )

  const getIsTransparent = useCallback(
    (guid: string, elementType?: ElementTypes) => {
      if (!translucent) return false

      // Priority: guidToTransparent has higher priority than visibilityByType
      const isTransparentForGuid = guidToTransparent[guid]
      if (isTransparentForGuid !== undefined) {
        return isTransparentForGuid
      }

      // Fallback to type-based transparency
      if (!elementType) return false
      return transparencyVisibilityByType[elementType] || false
    },
    [translucent, transparencyVisibilityByType, guidToTransparent],
  )

  const guidToColor = useSceneStore(state => state.guidToColor)

  const getShapeColor = useCallback(
    (
      { guid, element_type: type }: ShapeObject,
      highlightedSecondary: boolean,
      selected: boolean,
    ) => {
      const mappedColor = guidToColor[guid]
      if (highlightedIds.has(guid)) return scenePalette.highlightElements
      if (type === 'vertical_slabs') return scenePalette.elements3d.vertical_slabs
      if (type === 'slabs') return scenePalette.elements3d.slabs
      if (type === 'vertical_roof_slabs') return scenePalette.elements3d.vertical_roof_slabs
      if (type === 'roof_slabs') return scenePalette.elements3d.roof_slabs
      if (type === 'foundation') {
        if (selected) return scenePalette.selection
        return scenePalette.elements3d.foundation
      }
      if (type === 'columns' && highlightedSecondary) return scenePalette.elements3d.targets
      if (type === 'outer_walls') return mappedColor || scenePalette.elements3d.externalWalls
      if (type === 'inner_walls') return mappedColor || scenePalette.elements3d.internalWalls

      return undefined
    },
    [
      guidToColor,
      highlightedIds,
      scenePalette.highlightElements,
      scenePalette.selection,
      scenePalette.elements3d.vertical_slabs,
      scenePalette.elements3d.slabs,
      scenePalette.elements3d.vertical_roof_slabs,
      scenePalette.elements3d.roof_slabs,
      scenePalette.elements3d.foundation,
      scenePalette.elements3d.targets,
      scenePalette.elements3d.externalWalls,
      scenePalette.elements3d.internalWalls,
    ],
  )

  const getMemberColor = useCallback(
    ({ guid, element_type: type }: ShapeObjectLine, highlightedSecondary: boolean) => {
      const mappedColor = guidToColor[guid]
      if (highlightedSecondary) return scenePalette.elements3d.targets

      // Check if this element has a steel cross-section
      const csAssignment = guidToCrossSection[guid]
      const crossSection = csAssignment?.element_cs
      const isSteel = crossSection?.material?.kind === 'Steel'
      const steelColor = scenePalette.elements3d.steelBeams

      if (type === 'columns')
        return mappedColor || (isSteel ? steelColor : scenePalette.elements3d.columns)
      if (type === 'beams')
        return mappedColor || (isSteel ? steelColor : scenePalette.elements3d.beams)
      if (type === 'purlins')
        return mappedColor || (isSteel ? steelColor : scenePalette.elements3d.beams)
    },
    [
      guidToColor,
      guidToCrossSection,
      scenePalette.elements3d.beams,
      scenePalette.elements3d.steelBeams,
      scenePalette.elements3d.columns,
      scenePalette.elements3d.targets,
    ],
  )

  const getTypeMaterial = useCallback(
    ({ guid, element_type: type }: ShapeObject, selected: boolean) => {
      const transparencyOpacity = getTransparencyOpacity(guid, type as ElementTypes)
      const isTransparent = getIsTransparent(guid, type as ElementTypes)

      if (type === 'vertical_slabs' || type === 'vertical_roof_slabs') {
        // Use transparency opacity if enabled, otherwise use default 0.5
        const opacity = isTransparent ? transparencyOpacity : selected ? 1 : 0.5
        return {
          opacity,
          transparent: true,
          polygonOffset: true,
          polygonOffsetFactor: typeToPolygonOffsetFactor[type],
          polygonOffsetUnit: 1,
        }
      } else if (type === 'slabs' || type === 'roof_slabs') {
        // Use transparency opacity if enabled, otherwise use default 0.7
        const opacity = isTransparent ? transparencyOpacity : 0.7
        return {
          opacity,
          transparent: isTransparent || !selected,
          polygonOffset: true,
          polygonOffsetFactor: typeToPolygonOffsetFactor[type],
          polygonOffsetUnit: 1,
          doubleSided: true,
        }
      }

      return {}
    },
    [getTransparencyOpacity, getIsTransparent],
  )

  const getOutlinesColor = useCallback(
    (
      { element_type: type }: ShapeObject | ShapeObjectLine,
      selected: boolean,
      highlightedSecondary: boolean,
    ): string | undefined => {
      if (type === 'slabs' || type === 'roof_slabs') {
        if (selected) return scenePalette.highlightDark as string
        if (highlightedSecondary) return scenePalette.elements3d.targets as string
        return scenePalette.outlinesDark as string
      }

      if (selected) return scenePalette.highlightDark as string
      if (highlightedSecondary) return scenePalette.elements3d.targets as string

      return scenePalette.outlines as string
    },
    [
      scenePalette.elements3d.targets,
      scenePalette.highlightDark,
      scenePalette.outlines,
      scenePalette.outlinesDark,
    ],
  )

  // when clicking in the scene we want to e.g. "prioritize" vertical_slabs over
  // slabs. So in general when you click on a vertical_slab you select the
  // vertical_slab and not the underlying slab. If we do not offset the points a
  // tiny bit this is really random and not consistent. Sometimes you select the
  // slab and sometimes you select the vertical_slab which is really confusing.
  // We need to calculate this outside the render method in order to prevent
  // recreating slab on every render.
  const offsettedElementPoints = useMemo(() => {
    const nonBeamElements = [
      ...model.walls,
      ...model.roof_slabs,
      ...model.slabs,
      ...model.vertical_slabs,
      ...model.vertical_roof_slabs,
      ...model.foundationElements,
    ]
    return reduce(
      nonBeamElements,
      (col, element) => {
        let movedPoints = element.shape.points

        if (element.element_type === 'vertical_roof_slabs') {
          movedPoints = element.shape.points.map(
            point =>
              new ImmutableVector3(point.x, point.y, point.z + sceneOffsets.verticalRoofSlabs.z),
          )
        }

        if (element.element_type === 'vertical_slabs') {
          movedPoints = element.shape.points.map(
            point => new ImmutableVector3(point.x, point.y, point.z + sceneOffsets.verticalSlabs.z),
          )
        }

        return {
          ...col,
          [element.guid]: movedPoints,
        }
      },
      {} as Record<string, ImmutableVector3[]>,
    )
  }, [
    model.foundationElements,
    model.roof_slabs,
    model.slabs,
    model.vertical_roof_slabs,
    model.vertical_slabs,
    model.walls,
    sceneOffsets.verticalRoofSlabs.z,
    sceneOffsets.verticalSlabs.z,
  ])

  const lineLikeElements = useMemo(
    () => [...model.beams, ...model.columns, ...model.purlins],
    [model.beams, model.columns, model.purlins],
  )
  const shapeLikeElements = useMemo(
    () => [
      ...model.walls,
      ...model.roof_slabs,
      ...model.slabs,
      ...model.vertical_slabs,
      ...model.vertical_roof_slabs,
      ...model.foundationElements,
    ],
    [
      model.walls,
      model.roof_slabs,
      model.slabs,
      model.vertical_slabs,
      model.vertical_roof_slabs,
      model.foundationElements,
    ],
  )

  if (invisible) return <></>

  return (
    <group ref={groupRef} name={modelName}>
      {lineLikeElements.map(element => {
        const {
          guid,
          element_type: type,
          domains = [],
          coordinate_system,
          shape: { start, end },
        } = element

        const visible = !hiddenElements.has(element.guid)
        const selected = selectedElementIds.has(element.guid)
        const highlightedSecondary = highlightedSecondaryIds.has(element.guid)

        const lineWidth = selected ? 2 : 1

        const csAssignment = guidToCrossSection[guid]
        const crossSection = csAssignment?.element_cs
        const csShape = crossSection?.shape
        const csHeight = csShape?.height || beamThickness
        const csWidth = csShape?.width || beamThickness
        const opacity = getTransparencyOpacity(guid, type as ElementTypes)
        const showOutlines = opacity === 1

        return (
          <Fragment key={guid}>
            <MemberMeshElement
              key={guid}
              name={guid}
              start={start}
              end={end}
              height={csHeight}
              width={csWidth}
              coordinateSystem={coordinate_system}
              color={getMemberColor(element, highlightedSecondary)}
              opacity={opacity}
              visible={visible}
              cursor={interactableByType[type as ElementTypes] ? 'pointer' : undefined}
              noPointerInteractions={
                !interactableByType[type as ElementTypes] || noPointerInteractions
              }
              onPointerMove={onPointerOver}
              onPointerLeave={onPointerLeave}
              outlinesColor={getOutlinesColor(element, selected, highlightedSecondary)}
              lineWidth={lineWidth}
              onClick={onClick}
              onPointerUp={openContextMenuViaEvent}
              crossSectionShape={csShape}
              userData={{
                elementType: type,
                isBlocking: type !== undefined ? blockingElements[type] : undefined,
                snapGeometry: 'centerLine',
                start,
                end,
                contextMenuActions: {
                  hide: {
                    guid: element.guid,
                  },
                  properties: {
                    guid: element.guid,
                  },
                },
              }}
              outlines={showOutlines}
            />

            {selected && visible && (
              <DomainMesh domains={domains} width={0.02} color={scenePalette.highlightDark} />
            )}
            {highlightedSecondary && visible && (
              <DomainMesh domains={domains} width={0.02} color={scenePalette.elements3d.targets} />
            )}
          </Fragment>
        )
      })}

      {shapeLikeElements.map(element => {
        const {
          guid,
          element_type: type,
          domains = [],
          color,
          shape: { points: originalPoints },
        } = element

        const points = offsettedElementPoints[guid]
        // Don't render shapes with < 3 points
        if (points.length < 3) return <></>

        const visible = !hiddenElements.has(element.guid)
        const selected = selectedElementIds.has(element.guid)
        const highlightedSecondary = highlightedSecondaryIds.has(element.guid)
        const isFoundation = type === 'foundation'
        const renderDomainsAsSelected = !isFoundation && selected && visible
        const renderDomainsAsHighlightedSecondary = !isFoundation && highlightedSecondary && visible
        const opacity = getTransparencyOpacity(guid, type as ElementTypes)
        const showOutlines = opacity === 1
        const clipPlaneNormal = getPolygonNormal(originalPoints)
        const clipPlanePoint = originalPoints[0]
        // Body is drawn by BatchedShapesMesh (ModelMeshV2) when batching is on;
        // skip it here but keep this element's overlays/labels below.
        const skipBody = skipBatchedShapes && BATCHED_SHAPE_TYPES.has(type as ElementTypes)

        return (
          <Fragment key={guid}>
            {!skipBody && (
              <ShapeMesh
                key={guid}
                data={{
                  ...element,
                  shape: {
                    ...element.shape,
                    points,
                  },
                }}
                translucent={translucent}
                opacity={opacity}
                visible={!invisible && visible}
                cursor={interactableByType[type as ElementTypes] ? 'pointer' : undefined}
                noPointerInteractions={
                  !interactableByType[type as ElementTypes] || noPointerInteractions
                }
                shapeColor={getShapeColor(element, highlightedSecondary, selected) || color}
                onPointerMove={onPointerOver}
                onPointerLeave={onPointerLeave}
                outlinesColor={getOutlinesColor(element, selected, highlightedSecondary)}
                onClick={onClick}
                onPointerUp={openContextMenuViaEvent}
                applyThickness={applyThickness}
                userData={{
                  elementType: type,
                  isBlocking: type !== undefined ? blockingElements[type] : undefined,
                  points: originalPoints,
                  contextType: 'shape-mesh',
                  contextMenuActions: {
                    hide: {
                      guid: element.guid,
                    },
                    properties: {
                      guid: element.guid,
                    },
                    'clip-plane': {
                      normal: clipPlaneNormal,
                      point: clipPlanePoint,
                    },
                  },
                }}
                meshMaterialProps={getTypeMaterial(element, selected)}
                outlines={showOutlines}
              />
            )}

            {renderDomainsAsSelected && (
              <DomainMesh domains={domains} width={0.02} color={scenePalette.highlightDark} />
            )}
            {renderDomainsAsHighlightedSecondary && (
              <DomainMesh domains={domains} width={0.02} color={scenePalette.elements3d.targets} />
            )}
            {(type === 'vertical_slabs' || type === 'vertical_roof_slabs') &&
              !invisible &&
              visible && (
                <>
                  <ArrowOrientationSlab slab={element} opacity={opacity} />
                  <SnowLoadIndicator slab={element} opacity={opacity} />

                  {type === 'vertical_slabs' && <LiveLoadIndicator slab={element} />}
                  <ManualLoadIndicator slab={element} opacity={opacity} />
                </>
              )}
          </Fragment>
        )
      })}
      {model.walls.length && (
        <mesh>
          <Html
            style={{
              display: 'none',
            }}
          >
            <div data-cy="model-rendered" />
          </Html>
        </mesh>
      )}
    </group>
  )
}

export default memo(ModelMesh)
